import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'

import {
  ALL_RATIO_CONFIGS_QUERY,
  SINGLE_RATIO_CONFIG_QUERY,
  CREATE_RATIO_CONFIG_MUTATION,
  UPDATE_RATIO_CONFIG_MUTATION,
  DELETE_RATIO_CONFIG_MUTATION,
  FORCE_DELETE_RATIO_CONFIG_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const ratioConfigRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_RATIO_CONFIG_QUERY, variables).then((data: any) => {
      return hydrateObject(data.ratioConfig)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_RATIO_CONFIGS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.ratioConfigs)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_RATIO_CONFIG_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.ratioConfig),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_RATIO_CONFIGS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.ratioConfigs),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_RATIO_CONFIG_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.ratioConfig)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_RATIO_CONFIG_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.ratioConfig)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_RATIO_CONFIG_MUTATION, variables).then((data: any) => {
      return data.deleteRatioConfig
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_RATIO_CONFIG_MUTATION, variables).then((data: any) => {
      return data.forceDeleteRatioConfig
    })
  },
}

export default ratioConfigRepository
